import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Alert, Button, Spin } from 'antd'
import { useSelector } from 'react-redux'
import { getToken } from '@/global.config'
import { PHONE_CHANNEL, createPhoneBridge, phoneFrameUrl } from './phone-bridge.mjs'
import {
  canChangePhoneMode,
  createInitialPhoneState,
  reducePhoneState,
} from './phone-lifecycle.mjs'
import { phoneWorkspacePresentation } from './aicc-ui.mjs'
import './aicc.css'

export type PhoneMode = 'auto' | 'collaboration'

type PhoneStatus = {
  state: string
  activeCall: boolean
  agentState: string
}

type PhoneContextValue = {
  mode?: PhoneMode
  status: PhoneStatus
  visible: boolean
  ensureOpen: (mode: PhoneMode) => boolean
  leaveWorkspace: (mode: PhoneMode) => void
  canSwitchTo: (mode: PhoneMode) => boolean
}

const PhoneContext = createContext<PhoneContextValue>({
  status: { state: 'loading', activeCall: false, agentState: '' },
  visible: false,
  ensureOpen: () => false,
  leaveWorkspace: () => {},
  canSwitchTo: () => false,
})

export const useNextPhone = () => useContext(PhoneContext)

export default function PhoneProvider({ children }: { children: ReactNode }) {
  const profile = useSelector((reduxState: any) => reduxState.user.userInfo)
  const [state, dispatch] = useReducer(
    reducePhoneState,
    undefined,
    createInitialPhoneState
  )
  const [visibleMode, setVisibleMode] = useState<PhoneMode>()
  const frame = useRef<HTMLIFrameElement>(null)
  const bridgeRef = useRef<ReturnType<typeof createPhoneBridge> | null>(null)
  const sessionToken = useRef('')
  const modeRef = useRef<PhoneMode | undefined>(state.mode)
  const statusRef = useRef<PhoneStatus>(state.status)
  modeRef.current = state.mode
  statusRef.current = state.status

  // iframe 加载期间浏览器会先画一张白色画布；在电话页首次回消息（ready/status）前用暗色加载层盖住。
  const [reloadNonce, setReloadNonce] = useState(0)
  const frameKey = `${state.mode || ''}:${reloadNonce}`
  const [readyKey, setReadyKey] = useState('')
  const [timedOutKey, setTimedOutKey] = useState('')
  const frameReady = state.opened && readyKey === frameKey
  const frameTimedOut = !frameReady && timedOutKey === frameKey

  useEffect(() => {
    if (!state.opened || !state.mode) return
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return
      if (event.data?.channel === PHONE_CHANNEL && ['ready', 'status'].includes(event.data?.type)) setReadyKey(frameKey)
    }
    // 兜底：20 秒内电话页仍未响应，提示超时并允许重新加载
    const timeout = window.setTimeout(() => setTimedOutKey(frameKey), 20000)
    window.addEventListener('message', receive)
    return () => {
      window.clearTimeout(timeout)
      window.removeEventListener('message', receive)
    }
  }, [frameKey, state.opened, state.mode])

  const frameLoaded = useCallback(() => {
    // 页面已加载但未走桥接协议时，稍后也要显示出来，避免加载层常驻
    const key = frameKey
    window.setTimeout(() => setReadyKey(current => current || key), 6000)
  }, [frameKey])

  const reloadFrame = useCallback(() => {
    setTimedOutKey('')
    setReloadNonce(value => value + 1)
  }, [])

  const authorized = useCallback(
    (mode: PhoneMode) => {
      if (profile?.menus?.includes('aicc_workbench') !== true) return false
      if (!profile?.principal_type || profile.principal_type === 'legacy') return mode !== 'auto' || profile?.pds_enabled === true
      const line = mode === 'auto' ? 'pds' : 'collaboration'
      const capability = mode === 'auto' ? 'pds.seat.use' : 'collab.seat.use'
      const scope = profile?.scopes?.[line]
      const scoped = scope && (scope.organization === true || scope.self === true || ['managed_groups','own_groups','accounts'].some(key => Array.isArray(scope[key]) && scope[key].length > 0))
      return profile?.business_lines?.includes(line) === true && profile?.capabilities?.includes(capability) === true && scoped && (mode !== 'auto' || profile?.pds_enabled === true)
    },
    [profile]
  )

  const canSwitchTo = useCallback(
    (mode: PhoneMode) =>
      authorized(mode) &&
      (!state.mode || state.mode === mode || canChangePhoneMode(state.status)),
    [authorized, state.mode, state.status]
  )

  const ensureOpen = useCallback(
    (mode: PhoneMode) => {
      if (!getToken() || !canSwitchTo(mode)) return false
      if (!sessionToken.current) sessionToken.current = getToken() || ''
      dispatch({ type: 'OPEN', mode })
      setVisibleMode(mode)
      return true
    },
    [canSwitchTo]
  )

  const leaveWorkspace = useCallback((mode: PhoneMode) => {
    setVisibleMode(current => (current === mode ? undefined : current))
  }, [])

  useEffect(() => {
    if (!state.opened || !state.mode) return
    const bridge = createPhoneBridge({
      origin: window.location.origin,
      getFrame: () => frame.current?.contentWindow,
      getToken: () =>
        getToken() === sessionToken.current ? sessionToken.current : '',
      getMode: () => modeRef.current,
      onStatus: status => dispatch({ type: 'STATUS', status }),
      onClear: () => {
        sessionToken.current = ''
        setVisibleMode(undefined)
        dispatch({ type: 'CLOSE' })
      },
    })
    bridgeRef.current = bridge
    const receive = (event: MessageEvent) => bridge.receive(event)
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (statusRef.current.activeCall) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('message', receive)
    window.addEventListener('beforeunload', beforeUnload)
    const timer = window.setInterval(() => {
      if (!sessionToken.current || getToken() !== sessionToken.current) {
        bridge.destroy()
        sessionToken.current = ''
        setVisibleMode(undefined)
        dispatch({ type: 'CLOSE' })
      }
    }, 1000)
    return () => {
      bridge.destroy()
      bridgeRef.current = null
      window.clearInterval(timer)
      window.removeEventListener('message', receive)
      window.removeEventListener('beforeunload', beforeUnload)
    }
  }, [state.opened, state.mode])

  useEffect(() => {
    if (state.opened && state.mode && !authorized(state.mode)) {
      bridgeRef.current?.destroy()
      sessionToken.current = ''
      setVisibleMode(undefined)
      dispatch({ type: 'CLOSE' })
    }
  }, [authorized, state.mode, state.opened])

  const value = useMemo(
    () => ({
      mode: state.mode,
      status: state.status,
      visible: visibleMode === state.mode,
      ensureOpen,
      leaveWorkspace,
      canSwitchTo,
    }),
    [state.mode, state.status, visibleMode, ensureOpen, leaveWorkspace, canSwitchTo]
  )
  const presentation = state.mode ? phoneWorkspacePresentation(state.mode, state.status) : null

  return (
    <PhoneContext.Provider value={value}>
      {children}
      {state.opened && state.mode && (
        <section
          className={`next-phone-workspace-frame ${visibleMode === state.mode ? 'is-visible' : ''}`}
          aria-label="Voice Operations agent phone"
        >
          <header className="next-phone-frame-header">
            <div>
              <span className="next-aicc-eyebrow">{presentation?.eyebrow}</span>
              <strong>{presentation?.title}</strong>
              <small>{presentation?.description}</small>
            </div>
            <div className="next-phone-frame-status">
              <span className="next-status-chip blue">{presentation?.modeLabel}</span>
              <span className={`next-status-chip ${presentation?.tone || 'neutral'}`}><i />{presentation?.stateLabel}</span>
            </div>
          </header>
          {!sessionToken.current && <Alert type="warning" showIcon message="Phone session expired. Please sign in again." />}
          <div className="next-phone-frame-body">
            {!frameReady && (
              <div className="next-phone-frame-loading" role="status" aria-live="polite">
                {frameTimedOut ? <>
                  <strong>{presentation?.title} timed out</strong>
                  <span>The phone page is not responding. Check your network and try again.</span>
                  <Button onClick={reloadFrame}>Reload</Button>
                </> : <>
                  <Spin size="large" />
                  <span>Loading {presentation?.title}…</span>
                </>}
              </div>
            )}
            <iframe
              key={`${state.instanceId}:${reloadNonce}`}
              ref={frame}
              className={frameReady ? 'is-ready' : ''}
              title="llmbot Voice Operations agent phone"
              src={phoneFrameUrl(state.mode)}
              allow="microphone"
              referrerPolicy="same-origin"
              onLoad={frameLoaded}
            />
          </div>
        </section>
      )}
    </PhoneContext.Provider>
  )
}
